import { useMemo } from 'react';
import { useAppSelector, useMetricFormatter } from 'src/utils/Hooks';
import { removeDspClicks } from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/serverProxy/useBaseMetricRequestBuilder';
import { onDisplayAdvertisingMode } from 'src/store/modules/user/selectors';
import useGenericAdvancedSearch, { UseGenericAdvancedSearchParameters } from 'src/utils/Hooks/useGenericAdvancedSearch';
import { calculatePercentChange, getSearchAppName } from 'src/utils/app';
import { INDEX_FIELDS, METRICTYPE } from 'src/utils/entityDefinitions';
import AdvancedSearchRequestBuilder, {
  useAdvancedSearchRequestBuilder
} from 'src/components/BeaconV2Components/utils/AdvancedSearchRequestBuilder';
import AggregationBuilder from 'src/components/BeaconV2Components/utils/AggregationBuilder';
import { ValueOf } from 'sl-api-connector';
import {
  useApplyEntityFilters,
  useRemoveRetailPriceRangeFilters
} from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/serverProxy/useBaseMetricRequestBuilder';
import { SplineChartV2DataPoint, SplineChartV2Series } from './SplineChartV2';
import { calculateMetricTotal, calculateComparisonTotal } from './metricTotalUtils';
import { useComparisonWeekIds } from './useComparisonWeekIds';

/**
 * Arguments for useMetricByWeekIdV2 hook
 */
export interface UseMetricByWeekIdV2Args {
  /** The index name to query (e.g., 'sales', 'traffic') */
  indexName: string;
  /** The field name to query (e.g., 'retailSales', 'unitsSold') */
  fieldName: string;
  /** Optional different index for comparison data */
  comparisonIndexName?: string;
  /** Optional different field for comparison data */
  comparisonFieldName?: string;
  /** Entity type for field lookup */
  entityType?: string;
  /**
   * Allows optionally modifying the primary aggregation builder
   * before the request is fired
   */
  primaryAggregationBuilder?: (aggregationBuilder: AggregationBuilder) => AggregationBuilder;
  /**
   * Allows optionally modifying the secondary aggregation builder
   * before the request is fired
   */
  secondaryAggregationBuilder?: (aggregationBuilder: AggregationBuilder) => AggregationBuilder;
  /**
   * Allows optionally modifying the request builder before the request is fired
   */
  requestBuilder?: (requestBuilder: AdvancedSearchRequestBuilder, isComparison: boolean) => AdvancedSearchRequestBuilder;
  /**
   * Override metric type for formatting
   */
  metricTypeOverride?: ValueOf<typeof METRICTYPE>;
  /**
   * Additional args for useGenericAdvancedSearch
   */
  useGenericAdvancedSearchArgs?: Partial<UseGenericAdvancedSearchParameters>;
}

/**
 * Response type from the Advanced Search API
 */
interface AdvancedSearchByWeekIdResponse<TAdditionalValues> {
  aggregations: {
    by_weekId: {
      fieldId: string;
      additionalValues: TAdditionalValues;
    }[];
  };
}

/**
 * Raw row from the API with weekId and metric values
 */
interface MetricRow {
  weekId: number;
  [key: string]: number;
}

/**
 * Helper function to get a field by index name and field name
 */
const getField = (indexName: string, fieldName: string, entityType?: string) => {
  return INDEX_FIELDS.getField(getSearchAppName(), indexName, fieldName, entityType);
};

/**
 * Get the key from the response based on field configuration
 */
const getMetricKey = (indexName: string, fieldName: string, entityType?: string) => {
  const field = getField(indexName, fieldName, entityType);
  const keyEnding = field.aggregationFunction === 'stats' ? 'avg' : 'value';
  return `${fieldName}_${field.aggregationFunction}_${keyEnding}`;
};

/**
 * Converts raw API rows to SplineChartV2DataPoint array
 */
const toSplineChartV2Data = (
  rows: MetricRow[],
  indexName: string,
  fieldName: string,
  entityType?: string
): SplineChartV2DataPoint[] => {
  if (!rows?.length) return [];

  const metricKey = getMetricKey(indexName, fieldName, entityType);

  return rows
    .sort((a, b) => a.weekId - b.weekId)
    .map((row) => ({
      weekId: row.weekId,
      value: row[metricKey] ?? null
    }));
};

/**
 * Return type for the useMetricByWeekIdV2 hook
 */
export interface UseMetricByWeekIdV2Result {
  // ============ Series Data (ready for SplineChartV2) ============
  /** Primary series data ready for SplineChartV2 */
  primarySeries: SplineChartV2Series;
  /** Comparison series data ready for SplineChartV2 (shifted to current year weekIds if applicable) */
  comparisonSeries: SplineChartV2Series;

  // ============ Raw Data Points ============
  /** Raw primary data points */
  primaryData: SplineChartV2DataPoint[];
  /** Raw comparison data points */
  comparisonData: SplineChartV2DataPoint[];

  // ============ Loading State ============
  /** Whether the data is loading */
  isLoading: boolean;

  // ============ Field Info ============
  /** The metric type for this field */
  metricType: ValueOf<typeof METRICTYPE>;
  /** The field configuration from INDEX_FIELDS */
  field: ReturnType<typeof getField>;

  // ============ Raw Numeric Values ============
  /** Total value for primary data */
  primaryTotal: number;
  /** Total value for comparison data */
  comparisonTotal: number;
  /** Percent change between primary and comparison totals */
  percentChange: number | undefined;
  /** Last primary value */
  lastPrimaryValue: number | null;
  /** Last comparison value */
  lastComparisonValue: number | null;
  /** Percent change between last values */
  lastValuePercentChange: number | undefined;

  // ============ Formatted Values (display-ready strings) ============
  /** Formatted primary total (e.g., "$1.2M", "45.2%") */
  formattedPrimaryTotal: string;
  /** Formatted comparison total */
  formattedComparisonTotal: string;
  /** Formatted percent change (e.g., "12.5%") */
  formattedPercentChange: string;
  /** Formatted last primary value */
  formattedLastPrimaryValue: string;
  /** Formatted last comparison value */
  formattedLastComparisonValue: string;
  /** Formatted percent change between last values */
  formattedLastValuePercentChange: string;

  // ============ Time Period Info ============
  /** Whether this is a YTD with Prior Year comparison (comparison period is expanded to full year) */
  isYTDWithPriorYear: boolean;
  /** Whether this is Last Week with Prior Year comparison (main period is expanded to YTD) */
  isLastWeekWithPriorYear: boolean;
  /** The start weekId for the primary period */
  primaryStartWeekId: number;
  /** The end weekId for the primary period */
  primaryEndWeekId: number;
  /** The start weekId for the comparison period (may be expanded for YTD + Prior Year) */
  comparisonStartWeekId: number;
  /** The end weekId for the comparison period (may be expanded for YTD + Prior Year) */
  comparisonEndWeekId: number;
}

/**
 * A simplified hook for fetching metric data by weekId that returns
 * data in SplineChartV2 format with minimal transformations.
 *
 * Key differences from useMetricByWeekId:
 * - Returns data as SplineChartV2DataPoint[] (simple { weekId, value } objects)
 * - Returns pre-built SplineChartV2Series objects ready to pass to SplineChartV2
 * - Handles prior year comparison shifting internally
 * - Simpler return structure focused on what SplineChartV2 needs
 */
export const useMetricByWeekIdV2 = ({
  indexName,
  fieldName,
  comparisonFieldName,
  comparisonIndexName,
  entityType,
  primaryAggregationBuilder,
  secondaryAggregationBuilder,
  requestBuilder,
  metricTypeOverride,
  useGenericAdvancedSearchArgs = {}
}: UseMetricByWeekIdV2Args): UseMetricByWeekIdV2Result => {
  const applyEntityFilters = useApplyEntityFilters();
  const removeRetailPriceRangeFilters = useRemoveRetailPriceRangeFilters(indexName);
  const formatMetric = useMetricFormatter();
  const isAdvertisingDisplayMode = useAppSelector(onDisplayAdvertisingMode);

  // Use the new hook that fetches comparison week IDs from the microservice API
  // This properly handles 52/53 week year mappings
  const {
    startWeekId,
    endWeekId,
    comparisonStartWeekId,
    comparisonEndWeekId,
    isLoadingComparisonWeeks,
    isYTDWithPriorYear,
    isLastWeekWithPriorYear,
    comparisonWeekIdsForMainPeriod
  } = useComparisonWeekIds();

  const entity = useAppSelector((state) => state?.entityService?.mainEntity);
  const requestIdWithEntityInfo = `useMetricByWeekIdV2-${indexName}-${fieldName}-${entity?.type}-${entity?.id}`;
  const retailerId = useAppSelector((state) => state.retailer.id);

  // Field configuration
  const field = useMemo(
    () => INDEX_FIELDS.getField(getSearchAppName(), indexName, fieldName, entityType),
    [indexName, fieldName, entityType]
  );

  // Request builder base
  const baseRequestBuilderFromHook = useAdvancedSearchRequestBuilder(
    requestIdWithEntityInfo,
    `${getSearchAppName()}-${indexName}`
  );

  const baseRequestBuilder = useMemo(() => {
    let build = baseRequestBuilderFromHook
      .setDoAggregation(true)
      .setPageNumber(1)
      .setPageSize(1200)
      .setPeriod('year')
      .setReturnDocuments(false)
      .setSearchBy('parent');

    if (!isAdvertisingDisplayMode) {
      build = removeDspClicks(build);
    }

    return build;
  }, [baseRequestBuilderFromHook, isAdvertisingDisplayMode]);

  // Aggregation builder base
  const baseAggregationBuilder = useMemo(() => {
    return new AggregationBuilder('weekId').addConditionTermFilter('retailerId', [retailerId]);
  }, [retailerId]);

  // Main aggregation builder
  const mainAggregationBuilder = useMemo(() => {
    const aggBuilder = baseAggregationBuilder
      .clone()
      .addAggregationField(
        field.displayName,
        field.name,
        field.aggregationFunction,
        true,
        field.aggregationFunctionExpression
      )
      .addConditionRangeFilter('weekId', startWeekId, endWeekId);

    if (!isAdvertisingDisplayMode) {
      aggBuilder.addConditionTermFilter('searchTerm', ['[DSP Clicks]'], 'must_not');
    }

    if (field.dependentFields) {
      field.dependentFields.forEach((dependentField) => {
        aggBuilder.addAggregationField(
          dependentField.displayName,
          dependentField.name,
          dependentField.aggregationFunction,
          true
        );
      });
    }

    return primaryAggregationBuilder ? primaryAggregationBuilder(aggBuilder.clone()) : aggBuilder;
  }, [
    baseAggregationBuilder,
    field,
    startWeekId,
    endWeekId,
    isAdvertisingDisplayMode,
    primaryAggregationBuilder
  ]);

  // Main request body
  const mainRequestBody = useMemo(() => {
    const body = baseRequestBuilder
      .clone()
      .addAggregation(mainAggregationBuilder.build())
      .apply(applyEntityFilters)
      .apply(removeRetailPriceRangeFilters)
      .addConditionRangeFilter('weekId', startWeekId, endWeekId);

    return requestBuilder ? requestBuilder(body, false).build() : body.build();
  }, [
    baseRequestBuilder,
    mainAggregationBuilder,
    applyEntityFilters,
    removeRetailPriceRangeFilters,
    startWeekId,
    endWeekId,
    requestBuilder
  ]);

  // Comparison aggregation builder
  const comparisonAggregationBuilder = useMemo(() => {
    const aggBuilder = baseAggregationBuilder
      .clone()
      .addAggregationField(
        field.displayName,
        field.name,
        field.aggregationFunction,
        true,
        field.aggregationFunctionExpression
      )
      .addConditionRangeFilter('weekId', comparisonStartWeekId, comparisonEndWeekId);

    if (field.dependentFields) {
      field.dependentFields.forEach((dependentField) => {
        aggBuilder.addAggregationField(
          dependentField.displayName,
          dependentField.name,
          dependentField.aggregationFunction,
          true
        );
      });
    }

    return secondaryAggregationBuilder ? secondaryAggregationBuilder(aggBuilder.clone()) : aggBuilder;
  }, [
    baseAggregationBuilder,
    field,
    comparisonStartWeekId,
    comparisonEndWeekId,
    secondaryAggregationBuilder
  ]);

  // Comparison request body
  const comparisonRequestBody = useMemo(() => {
    const body = baseRequestBuilder
      .clone()
      .addAggregation(comparisonAggregationBuilder.build())
      .apply(applyEntityFilters)
      .apply(removeRetailPriceRangeFilters)
      .addConditionRangeFilter('weekId', comparisonStartWeekId, comparisonEndWeekId);

    if (!isAdvertisingDisplayMode) {
      body.addConditionTermFilter('searchTerm', 'must_not', ['[DSP Clicks]']);
    }

    return requestBuilder ? requestBuilder(body, true).build() : body.build();
  }, [
    baseRequestBuilder,
    comparisonAggregationBuilder,
    applyEntityFilters,
    removeRetailPriceRangeFilters,
    comparisonStartWeekId,
    comparisonEndWeekId,
    isAdvertisingDisplayMode,
    requestBuilder
  ]);

  // Fetch data
  const { data: response, isLoading: isLoadingData } = useGenericAdvancedSearch<AdvancedSearchByWeekIdResponse<any>[]>({
    requestBody: [mainRequestBody, comparisonRequestBody],
    requestId: requestIdWithEntityInfo,
    queryKeys: [mainRequestBody, comparisonRequestBody],
    ...useGenericAdvancedSearchArgs
  });

  // Combined loading state (data + comparison week IDs from API)
  const isLoading = isLoadingData || isLoadingComparisonWeeks;

  // Parse main rows
  const mainRows: MetricRow[] = useMemo(() => {
    if (!response?.data?.[0]?.aggregations?.by_weekId) return [];
    return response.data[0].aggregations.by_weekId.map(({ fieldId, additionalValues }) => ({
      weekId: Number(fieldId),
      ...additionalValues
    }));
  }, [response]);

  // Parse comparison rows
  const comparisonRows: MetricRow[] = useMemo(() => {
    if (!response?.data?.[1]?.aggregations?.by_weekId) return [];
    return response.data[1].aggregations.by_weekId.map(({ fieldId, additionalValues }) => ({
      weekId: Number(fieldId),
      ...additionalValues
    }));
  }, [response]);

  // Convert to SplineChartV2 data format
  const primaryData = useMemo(
    () => toSplineChartV2Data(mainRows, indexName, fieldName, entityType),
    [mainRows, indexName, fieldName, entityType]
  );

  // No transformation needed - the chart maps by week number (weekId % 100)
  // so 202501 and 202601 will both map to week "01" category
  const comparisonData = useMemo(
    () => toSplineChartV2Data(comparisonRows, comparisonIndexName || indexName, comparisonFieldName || fieldName, entityType),
    [comparisonRows, comparisonIndexName, indexName, comparisonFieldName, fieldName, entityType]
  );

  // Calculate totals using the utility function
  // This follows the same logic as V1's getMetricTotalValue
  const primaryTotal = useMemo(() => {
    return calculateMetricTotal(primaryData, field, {
      isLastWeekWithPriorYear
    });
  }, [primaryData, field, isLastWeekWithPriorYear]);

  // For comparison total, only include the comparison weeks that correspond to the main period weeks.
  // The comparisonWeekIdsForMainPeriod array contains the API-mapped comparison week IDs
  // for each main period week, properly handling 52/53 week year mappings.
  const comparisonTotal = useMemo(() => {
    return calculateComparisonTotal(comparisonData, field, {
      isLastWeekWithPriorYear,
      // Only sum comparison weeks that correspond to main period weeks (from API)
      comparisonWeekIdsToInclude: comparisonWeekIdsForMainPeriod
    });
  }, [comparisonData, field, isLastWeekWithPriorYear, comparisonWeekIdsForMainPeriod]);

  const percentChange = useMemo(
    () => (comparisonTotal !== 0 ? calculatePercentChange(primaryTotal, comparisonTotal) : undefined),
    [primaryTotal, comparisonTotal]
  );

  // Last values
  const lastPrimaryValue = primaryData.length > 0 ? primaryData[primaryData.length - 1].value : null;
  const lastComparisonValue = comparisonData.length > 0 ? comparisonData[comparisonData.length - 1].value : null;
  const lastValuePercentChange =
    lastPrimaryValue !== null && lastComparisonValue !== null
      ? calculatePercentChange(lastPrimaryValue, lastComparisonValue)
      : undefined;

  // Build series for SplineChartV2
  const primarySeries: SplineChartV2Series = useMemo(
    () => ({
      id: `${indexName}-${fieldName}-primary`,
      name: field.displayName || fieldName,
      data: primaryData
    }),
    [indexName, fieldName, field.displayName, primaryData]
  );

  const comparisonSeries: SplineChartV2Series = useMemo(
    () => ({
      id: `${indexName}-${fieldName}-comparison`,
      name: `${field.displayName || fieldName} (Comparison)`,
      data: comparisonData
    }),
    [indexName, fieldName, field.displayName, comparisonData]
  );

  const metricType = metricTypeOverride || field.metricType;

  // ============ Formatted Values ============
  const formatOptions = {
    decimalPlaces: 2,
    showFullValue: metricType === METRICTYPE.PERCENT,
    showNegative: true
  };

  const formattedPrimaryTotal = useMemo(
    () => formatMetric(primaryTotal, metricType, formatOptions),
    [formatMetric, primaryTotal, metricType]
  );

  const formattedComparisonTotal = useMemo(
    () => formatMetric(comparisonTotal, metricType, formatOptions),
    [formatMetric, comparisonTotal, metricType]
  );

  const formattedPercentChange = useMemo(
    () =>
      percentChange !== undefined
        ? formatMetric(Math.abs(percentChange), METRICTYPE.PERCENT, { decimalPlaces: 2 })
        : '',
    [formatMetric, percentChange]
  );

  const formattedLastPrimaryValue = useMemo(
    () => (lastPrimaryValue !== null ? formatMetric(lastPrimaryValue, metricType, formatOptions) : ''),
    [formatMetric, lastPrimaryValue, metricType]
  );

  const formattedLastComparisonValue = useMemo(
    () => (lastComparisonValue !== null ? formatMetric(lastComparisonValue, metricType, formatOptions) : ''),
    [formatMetric, lastComparisonValue, metricType]
  );

  const formattedLastValuePercentChange = useMemo(
    () =>
      lastValuePercentChange !== undefined
        ? formatMetric(Math.abs(lastValuePercentChange), METRICTYPE.PERCENT, { decimalPlaces: 2 })
        : '',
    [formatMetric, lastValuePercentChange]
  );

  return {
    // Series data
    primarySeries,
    comparisonSeries,

    // Raw data points
    primaryData,
    comparisonData,

    // Loading state
    isLoading,

    // Field info
    metricType,
    field,

    // Raw numeric values
    primaryTotal,
    comparisonTotal,
    percentChange,
    lastPrimaryValue,
    lastComparisonValue,
    lastValuePercentChange,

    // Formatted values
    formattedPrimaryTotal,
    formattedComparisonTotal,
    formattedPercentChange,
    formattedLastPrimaryValue,
    formattedLastComparisonValue,
    formattedLastValuePercentChange,

    // Time period info
    isYTDWithPriorYear,
    isLastWeekWithPriorYear,
    primaryStartWeekId: startWeekId,
    primaryEndWeekId: endWeekId,
    comparisonStartWeekId,
    comparisonEndWeekId
  };
};

export default useMetricByWeekIdV2;
